// 不変条件の維持: スコープ確定書 §4.2 に準拠。
//   - 各 Roulette.sets は最低 1 個
//   - 各 ItemSet.items は最低 1 個
//   - currentSetId は必ず sets 内に存在
//   - activeRouletteId は必ず roulettes 内に存在
// すべての mutation 後に enforceInvariants(state) を通すことで担保する。

import type { AppState, RangeValue, Roulette } from './types';
import {
  defaultEffectSettings,
  defaultPreferences,
  defaultSettings,
  defaultUi,
  EFFECT_KEYS,
  makeItem,
  makeRoulette,
  makeSet,
} from './defaults';

export function enforceInvariants(state: AppState): AppState {
  if (state.roulettes.length === 0) {
    state.roulettes = [makeRoulette('ルーレット 1')];
  }
  for (const r of state.roulettes) {
    enforceRouletteInvariants(r);
  }
  if (!state.roulettes.some((r) => r.id === state.activeRouletteId)) {
    state.activeRouletteId = state.roulettes[0].id;
  }
  // 全ての visible が false になるのを防止 (最低 1 件は表示)
  if (!state.roulettes.some((r) => r.visible)) {
    state.roulettes[0].visible = true;
  }
  // ui の欠落フィールドをデフォルト補完
  state.ui = { ...defaultUi(), ...state.ui };
  // preferences が無い場合の移行: 旧 RouletteSettings.* / 旧 ui.themeMode から救出
  const prefDefaults = defaultPreferences();
  const oldFirstSet = state.roulettes[0]?.settings as unknown as Record<string, unknown> | undefined;
  const oldUiTheme = (state.ui as unknown as { themeMode?: string }).themeMode;
  if (!state.preferences) {
    state.preferences = {
      themeMode:
        (oldUiTheme === 'auto' || oldUiTheme === 'light' || oldUiTheme === 'dark')
          ? oldUiTheme
          : prefDefaults.themeMode,
      volume:
        typeof oldFirstSet?.volume === 'number' ? (oldFirstSet.volume as number) : prefDefaults.volume,
      showItemProbability:
        typeof oldFirstSet?.showItemProbability === 'boolean'
          ? (oldFirstSet.showItemProbability as boolean)
          : prefDefaults.showItemProbability,
      showItemWinCount:
        typeof oldFirstSet?.showItemWinCount === 'boolean'
          ? (oldFirstSet.showItemWinCount as boolean)
          : prefDefaults.showItemWinCount,
      randomMaintainEvenness: prefDefaults.randomMaintainEvenness,
    };
  } else {
    state.preferences = { ...prefDefaults, ...state.preferences };
  }
  // 旧 ui.themeMode を削除 (移行済みなので不要)
  delete (state.ui as unknown as { themeMode?: string }).themeMode;
  // shuffleOrder の妥当性チェック (項目数変化やセット切替で破棄)
  const so = state.ui.shuffleOrder;
  if (so) {
    // 旧データには mode フィールドが無いため 'item' とみなす
    const mode = (so as { mode?: string }).mode === 'split' ? 'split' : 'item';
    let valid = true;
    // setId に対応する set を探す (active かどうかは問わない)
    let foundSet: { items: { splitCount: number; enabled: boolean }[] } | undefined;
    for (const r of state.roulettes) {
      const s = r.sets.find((x) => x.id === so.setId);
      if (s) {
        foundSet = s;
        break;
      }
    }
    if (!foundSet) {
      valid = false;
    } else if (mode === 'item') {
      // item モード: order.length === items.length
      if (so.order.length !== foundSet.items.length) valid = false;
    } else {
      // split モード: order の各値が有効な item index で、合計が enabled な splitCount 合計と一致
      const expected = foundSet.items
        .filter((it) => it.enabled)
        .reduce((s, it) => s + Math.max(1, it.splitCount), 0);
      if (so.order.length !== expected) valid = false;
      else {
        const maxIdx = foundSet.items.length - 1;
        if (so.order.some((i) => i < 0 || i > maxIdx)) valid = false;
      }
    }
    if (!valid) state.ui.shuffleOrder = null;
  }
  return state;
}

function enforceRouletteInvariants(r: Roulette): void {
  if (r.sets.length === 0) {
    r.sets = [makeSet('デフォルト')];
  }
  for (const s of r.sets) {
    if (s.items.length === 0) {
      s.items = [makeItem('項目 1')];
    }
  }
  if (!r.sets.some((s) => s.id === r.currentSetId)) {
    r.currentSetId = r.sets[0].id;
  }
  // 旧スキーマで欠落しているフィールドをデフォルトで補完
  r.settings = { ...defaultSettings(), ...r.settings };
  // spinEffects の欠落フィールドを補完 (旧データ移行)
  if (!r.settings.spinEffects) {
    r.settings.spinEffects = defaultEffectSettings();
  } else {
    const def = defaultEffectSettings();
    r.settings.spinEffects = { ...def, ...r.settings.spinEffects };
    // 旧キー (charTarget/charExpect/charNg/markerPulse/vibraConfirm) を削除
    const effectsAny = r.settings.spinEffects.effects as Record<string, unknown>;
    delete effectsAny.charTarget;
    delete effectsAny.charExpect;
    delete effectsAny.charNg;
    delete effectsAny.markerPulse;
    delete effectsAny.vibraConfirm;
    // 新しいキーを補完 + 旧スキーマからの移行
    for (const key of EFFECT_KEYS) {
      if (!r.settings.spinEffects.effects[key]) {
        r.settings.spinEffects.effects[key] = def.effects[key];
        continue;
      }
      const cfg = r.settings.spinEffects.effects[key] as unknown as Record<string, unknown>;
      const defCfg = def.effects[key];
      // 旧 probability (number) → probabilityRange (RangeValue)
      if (typeof cfg.probability === 'number' && !cfg.probabilityRange) {
        cfg.probabilityRange = defCfg.probabilityRange;
        delete cfg.probability;
      }
      // 旧 timingRatio (number) → timingRange (RangeValue)
      if (typeof cfg.timingRatio === 'number' && !cfg.timingRange) {
        const t = cfg.timingRatio as number;
        cfg.timingRange = { min: Math.max(0, t - 0.10), max: Math.min(1, t + 0.10) };
        delete cfg.timingRatio;
      }
      // 旧 enabledVariants (boolean[]) → selectedVariant (number)。互換: 旧版は全部採用 = ランダム扱い
      if (Array.isArray(cfg.enabledVariants) && typeof cfg.selectedVariant !== 'number') {
        cfg.selectedVariant = 0;
        delete cfg.enabledVariants;
      }
      // 補完
      if (!cfg.probabilityRange || typeof (cfg.probabilityRange as RangeValue).min !== 'number') {
        cfg.probabilityRange = defCfg.probabilityRange;
      }
      if (!cfg.timingRange || typeof (cfg.timingRange as RangeValue).min !== 'number') {
        cfg.timingRange = defCfg.timingRange;
      }
      if (typeof cfg.selectedVariant !== 'number') {
        cfg.selectedVariant = 0;
      }
      // probabilityRandom / timingRandom は v6 以前にはなかったため false で補完
      if (typeof cfg.probabilityRandom !== 'boolean') cfg.probabilityRandom = false;
      if (typeof cfg.timingRandom !== 'boolean') cfg.timingRandom = false;
    }
  }
  if (typeof r.visible !== 'boolean') r.visible = true;
}
